import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  MapPin,
  DollarSign,
  User,
  Mail,
  Phone,
  FileText,
  ExternalLink,
  Save,
  AlertCircle,
  Sparkles,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { getReferralJobPublicUrl } from '../utils/referralJobUrl';

const SITE_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || 'https://rabotamax.ru';

interface InternalJob {
  id: number;
  user_id: number;
  title: string;
  company: string;
  location: string;
  salary?: string;
  description: string;
  requirements?: string;
  industry?: string;
  department?: string;
  contact_email: string;
  contact_phone?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;
  views_count: number;
  applications_count: number;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

interface ModerationResult {
  verdict: 'ok' | 'remarks' | 'rejected';
  recommendation: string;
  rejection_reason?: string | null;
}

const InternalVacancyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<InternalJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [moderationResult, setModerationResult] = useState<ModerationResult | null>(null);
  const [loadingModeration, setLoadingModeration] = useState(false);
  const [loadingGenerateSeo, setLoadingGenerateSeo] = useState(false);
  const [approveWithSeo, setApproveWithSeo] = useState(true);
  const [savingModeration, setSavingModeration] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingMain, setSavingMain] = useState(false);

  useEffect(() => {
    if (id) {
      loadJob();
    }
  }, [id]);

  useEffect(() => {
    if (job) {
      setSeoTitle(job.seo_title ?? '');
      setSeoDescription(job.seo_description ?? '');
      setSeoKeywords(job.seo_keywords ?? '');
      setRejectionReason(job.rejection_reason ?? '');
      setEditTitle(job.title);
      setEditCompany(job.company);
      setEditLocation(job.location);
      setEditDescription(job.description);
    }
  }, [job]);

  const loadJob = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/internal-vacancies/${id}`);
      if (response.data.success && response.data.job) {
        setJob(response.data.job);
      } else {
        toast.error('Вакансия не найдена');
        navigate('/vacancies/internal');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки вакансии');
      navigate('/vacancies/internal');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSeo = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await api.patch(`/admin/internal-vacancies/${id}`, {
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_keywords: seoKeywords.trim() || null,
      });
      toast.success('SEO-поля сохранены');
      setJob((prev) =>
        prev
          ? {
              ...prev,
              seo_title: seoTitle.trim() || null,
              seo_description: seoDescription.trim() || null,
              seo_keywords: seoKeywords.trim() || null,
            }
          : null
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModeration = async () => {
    if (!id) return;
    try {
      setSavingModeration(true);
      await api.patch(`/admin/internal-vacancies/${id}`, {
        rejection_reason: rejectionReason.trim() || null,
      });
      toast.success('Модерационные данные сохранены');
      setJob((prev) => (prev ? { ...prev, rejection_reason: rejectionReason.trim() || undefined } : null));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSavingModeration(false);
    }
  };

  const handleSaveMainFields = async () => {
    if (!id) return;
    const t = editTitle.trim();
    const c = editCompany.trim();
    const loc = editLocation.trim();
    const desc = editDescription.trim();
    if (!t || !c || !loc || !desc) {
      toast.error('Заполните название, компанию, город и описание');
      return;
    }
    try {
      setSavingMain(true);
      await api.patch(`/admin/internal-vacancies/${id}`, {
        title: t,
        company: c,
        location: loc,
        description: desc,
      });
      toast.success('Основные данные сохранены');
      setJob((prev) =>
        prev ? { ...prev, title: t, company: c, location: loc, description: desc } : null
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSavingMain(false);
    }
  };

  const handleModerateWithAi = async () => {
    if (!job) return;
    try {
      setLoadingModeration(true);
      setModerationResult(null);
      const response = await api.post('/ai/moderate-internal-vacancy', {
        title: job.title,
        company: job.company,
        location: job.location,
        salary: job.salary || '',
        description: job.description,
        requirements: job.requirements || '',
        industry: job.industry || '',
        department: job.department || '',
      });
      if (response.data?.success) {
        setModerationResult({
          verdict: response.data.verdict,
          recommendation: response.data.recommendation || '',
          rejection_reason: response.data.rejection_reason || null,
        });
      } else {
        toast.error(response.data?.message || 'Ошибка модерации');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Ошибка запроса к модели');
    } finally {
      setLoadingModeration(false);
    }
  };

  const handleApplyRejectionReason = async () => {
    if (!moderationResult?.rejection_reason) {
      toast.error('Нет причины отклонения для подстановки');
      return;
    }
    setRejectionReason(moderationResult.rejection_reason);
    if (!id) return;
    try {
      setSavingModeration(true);
      await api.patch(`/admin/internal-vacancies/${id}`, {
        rejection_reason: moderationResult.rejection_reason,
        status: 'rejected',
      });
      toast.success('Причина отклонения сохранена, статус — «Отклонена»');
      setJob((prev) =>
        prev ? { ...prev, status: 'rejected', rejection_reason: moderationResult.rejection_reason ?? undefined } : null
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSavingModeration(false);
    }
  };

  const handleGenerateSeo = async () => {
    if (!job) return;
    try {
      setLoadingGenerateSeo(true);
      const descSnippet = job.description.slice(0, 700);
      const jobUrl = getReferralJobPublicUrl(job.id, job.title, SITE_ORIGIN);
      const response = await api.post('/ai/generate-vacancy-seo', {
        title: job.title,
        company: job.company,
        location: job.location,
        description_snippet: descSnippet,
        salary: job.salary || null,
        experience: '',
        url: jobUrl,
      });
      if (response.data?.success) {
        setSeoTitle(response.data.seo_title || '');
        setSeoDescription(response.data.seo_description || '');
        setSeoKeywords(response.data.seo_keywords || '');
        toast.success('SEO сгенерировано');
      } else {
        toast.error(response.data?.message || 'Ошибка генерации SEO');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Ошибка генерации SEO');
    } finally {
      setLoadingGenerateSeo(false);
    }
  };

  const handleApproveAndPublish = async () => {
    if (!id || !job) return;
    try {
      setApproving(true);
      if (approveWithSeo) {
        const descSnippet = job.description.slice(0, 700);
        const jobUrl = getReferralJobPublicUrl(job.id, job.title, SITE_ORIGIN);
        const seoRes = await api.post('/ai/generate-vacancy-seo', {
          title: job.title,
          company: job.company,
          location: job.location,
          description_snippet: descSnippet,
          salary: job.salary || null,
          experience: '',
          url: jobUrl,
        });
        if (seoRes.data?.success) {
          await api.patch(`/admin/internal-vacancies/${id}`, {
            seo_title: seoRes.data.seo_title || null,
            seo_description: seoRes.data.seo_description || null,
            seo_keywords: seoRes.data.seo_keywords || null,
          });
          setSeoTitle(seoRes.data.seo_title || '');
          setSeoDescription(seoRes.data.seo_description || '');
          setSeoKeywords(seoRes.data.seo_keywords || '');
        }
      }
      await api.post(`/admin/internal-vacancies/${id}/approve`);
      toast.success('Вакансия одобрена и опубликована');
      setJob((prev) => (prev ? { ...prev, status: 'approved' as const } : null));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка одобрения');
    } finally {
      setApproving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!job || !window.confirm('Снять вакансию с публикации? Она перейдёт в статус «На модерации».')) return;
    try {
      await api.post(`/admin/internal-vacancies/${job.id}/unpublish`);
      toast.success('Вакансия снята с публикации');
      setJob((prev) => (prev ? { ...prev, status: 'pending' as const } : null));
    } catch (error: any) {
      toast.error('Ошибка при снятии с публикации');
    }
  };

  const loadApplications = async () => {
    if (!job) return;
    try {
      setLoadingApplications(true);
      const response = await api.get(`/admin/internal-vacancies/${job.id}/applications`);
      if (response.data.success) {
        setApplications(response.data.applications || []);
        setShowApplicationsModal(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки откликов');
    } finally {
      setLoadingApplications(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'На модерации';
      case 'approved':
        return 'Одобрена';
      case 'rejected':
        return 'Отклонена';
      default:
        return status;
    }
  };

  if (loading || !job) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const publicUrl = getReferralJobPublicUrl(job.id, job.title, SITE_ORIGIN);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/vacancies/internal')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        К списку вакансий
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
              {getStatusText(job.status)}
            </span>
          </div>
          {job.status === 'approved' && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Посмотреть на сайте
            </a>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Редактирование: название, компания, город, описание */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-4">Редактирование вакансии</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название вакансии</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Название вакансии"
                  maxLength={500}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Компания</label>
                  <input
                    type="text"
                    value={editCompany}
                    onChange={(e) => setEditCompany(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Название компании"
                    maxLength={255}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Город</label>
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Город"
                    maxLength={255}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[120px] resize-y"
                  placeholder="Описание вакансии"
                />
              </div>
              <button
                onClick={handleSaveMainFields}
                disabled={savingMain}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingMain ? 'Сохранение…' : 'Сохранить основные данные'}
              </button>
            </div>
          </div>

          {/* Основные поля (отображение) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center text-gray-700">
              <Building className="h-4 w-4 mr-2 text-gray-500" />
              {job.company}
            </div>
            <div className="flex items-center text-gray-700">
              <MapPin className="h-4 w-4 mr-2 text-gray-500" />
              {job.location}
            </div>
            {job.salary && (
              <div className="flex items-center text-gray-700">
                <DollarSign className="h-4 w-4 mr-2 text-gray-500" />
                {job.salary}
              </div>
            )}
            <div className="flex items-center text-gray-700">
              <User className="h-4 w-4 mr-2 text-gray-500" />
              {job.user_first_name && job.user_last_name
                ? `${job.user_first_name} ${job.user_last_name}`
                : job.user_email || '—'}
            </div>
            <div className="flex items-center text-gray-700">
              <Mail className="h-4 w-4 mr-2 text-gray-500" />
              {job.contact_email}
            </div>
            {job.contact_phone && (
              <div className="flex items-center text-gray-700">
                <Phone className="h-4 w-4 mr-2 text-gray-500" />
                {job.contact_phone}
              </div>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-2" />
              Описание
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
          </div>
          {job.requirements && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Требования</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{job.requirements}</p>
            </div>
          )}
          {(job.industry || job.department) && (
            <div className="flex flex-wrap gap-2">
              {job.industry && (
                <span className="bg-gray-100 px-2 py-1 rounded text-sm">{job.industry}</span>
              )}
              {job.department && (
                <span className="bg-gray-100 px-2 py-1 rounded text-sm">{job.department}</span>
              )}
            </div>
          )}

          {/* Редактируемая причина отклонения и модерация моделью */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Данные модерации</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Причина отклонения (видна пользователю в «Мои вакансии»)</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Текст причины отклонения"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20 resize-y"
                  maxLength={2000}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleSaveModeration}
                  disabled={savingModeration}
                  className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingModeration ? 'Сохранение…' : 'Сохранить модерационные данные'}
                </button>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Модерация моделью</h4>
              <button
                onClick={handleModerateWithAi}
                disabled={loadingModeration}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {loadingModeration ? 'Проверка…' : 'Промодерировать моделью'}
              </button>
              {moderationResult && (
                <div className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                  <div className="flex items-center gap-2">
                    {moderationResult.verdict === 'ok' && <CheckCircle className="h-5 w-5 text-green-600" />}
                    {moderationResult.verdict === 'remarks' && <AlertCircle className="h-5 w-5 text-amber-600" />}
                    {moderationResult.verdict === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                    <span className="font-medium">
                      {moderationResult.verdict === 'ok' && 'Всё в порядке'}
                      {moderationResult.verdict === 'remarks' && 'Есть замечания'}
                      {moderationResult.verdict === 'rejected' && 'Отклонена'}
                    </span>
                  </div>
                  {moderationResult.recommendation && (
                    <p className="text-sm text-gray-700">{moderationResult.recommendation}</p>
                  )}
                  {moderationResult.rejection_reason && (
                    <p className="text-sm text-red-700"><span className="font-medium">Причина для пользователя:</span> {moderationResult.rejection_reason}</p>
                  )}
                  {moderationResult.rejection_reason && (
                    <button
                      onClick={handleApplyRejectionReason}
                      disabled={savingModeration}
                      className="mt-2 px-3 py-1.5 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 text-sm font-medium disabled:opacity-50"
                    >
                      Подставить причину и отклонить вакансию
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SEO */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">SEO (страница вакансии на сайте)</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
                <input
                  type="text"
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder={`${job.title} — ${job.company} | Работа от своих | РаботаМакс`}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  maxLength={255}
                />
                <p className="text-xs text-gray-500 mt-1">Если пусто — подставится заголовок и компания</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SEO Description</label>
                <textarea
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder="Краткое описание для поисковиков (до 512 символов)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 resize-y"
                  maxLength={512}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SEO Keywords</label>
                <input
                  type="text"
                  value={seoKeywords}
                  onChange={(e) => setSeoKeywords(e.target.value)}
                  placeholder="Ключевые слова через запятую"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  maxLength={512}
                />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={handleGenerateSeo}
                  disabled={loadingGenerateSeo}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {loadingGenerateSeo ? 'Генерация…' : 'Сгенерировать SEO моделью'}
                </button>
                <button
                  onClick={handleSaveSeo}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Сохранение…' : 'Сохранить SEO'}
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900 text-sm"
                >
                  {publicUrl}
                </a>
              </div>
            </div>
          </div>

          {/* Действия */}
          <div className="border-t border-gray-200 pt-6 flex flex-wrap gap-3 items-center">
            {job.status === 'pending' && (
              <>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={approveWithSeo}
                    onChange={(e) => setApproveWithSeo(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Сгенерировать SEO перед публикацией</span>
                </label>
                <button
                  onClick={handleApproveAndPublish}
                  disabled={approving}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {approving ? 'Публикация…' : 'Одобрить и опубликовать'}
                </button>
              </>
            )}
            {job.status === 'approved' && (
              <button
                onClick={handleUnpublish}
                className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 font-medium"
              >
                Снять с публикации
              </button>
            )}
            <button
              onClick={loadApplications}
              disabled={loadingApplications}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Отклики ({job.applications_count ?? 0})
            </button>
          </div>
        </div>
      </div>

      {/* Applications modal */}
      {showApplicationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Отклики на вакансию</h3>
              <button
                onClick={() => setShowApplicationsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {applications.length === 0 ? (
                <p className="text-gray-500">Откликов пока нет</p>
              ) : (
                <ul className="space-y-4">
                  {applications.map((app: any) => (
                    <li key={app.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-900">
                        {app.user?.firstName} {app.user?.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{app.user?.email}</div>
                      {app.message && <p className="mt-2 text-gray-700">{app.message}</p>}
                      <div className="text-xs text-gray-500 mt-2">{new Date(app.createdAt).toLocaleString('ru-RU')}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalVacancyDetailPage;
